import React, { useContext, useEffect, useState, useRef } from 'react';
import { Table, Button, Space, message, Tag, Progress, Modal, InputNumber, Select, Input, Popconfirm, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FilterDropdownProps } from 'antd/es/table/interface';
import type { InputRef } from 'antd';
import { 
    fetchAllAcounts, 
    updateUserQuota, 
    updateUserRole, 
    updateUserEmail,
    resetUserPassword,
    deleteUserAccount,
    clearUserSubmissions 
} from '../../api';
import { AuthContext } from '../../AuthContext';
import { 
    UserOutlined, 
    DeleteOutlined, 
    LockOutlined,
    ClearOutlined,
    MailOutlined,
    SafetyOutlined,
    DashboardOutlined,
    SearchOutlined
} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';

interface DataType {
    username: string;
    email: string;
    agencyName: string;
    role: number;
    quotaLimit: number;
    quotaUsed: number;
}

const getRoleName = (role: number): string => {
    switch (role) {
        case 1:
            return '用戶';
        case 2:
            return '管理員';
        case 3:
            return '超級管理員';
        default:
            return '未知';
    }
};

const getRoleColor = (role: number): string => {
    switch (role) {
        case 1:
            return 'blue';
        case 2:
            return 'orange';
        case 3:
            return 'red';
        default:
            return 'default';
    }
};

type DataIndex = keyof DataType;

const AccountsPage = () => {
    const authContext = useContext(AuthContext);
    const { setLoggedIn } = authContext || {};
    const [accounts, setAccounts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    
    // Modal states
    const [quotaModalVisible, setQuotaModalVisible] = useState(false);
    const [roleModalVisible, setRoleModalVisible] = useState(false);
    const [emailModalVisible, setEmailModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    
    // Form states
    const [newQuotaLimit, setNewQuotaLimit] = useState<number>(0);
    const [newQuotaUsed, setNewQuotaUsed] = useState<number>(0);
    const [newRole, setNewRole] = useState<number>(1);
    const [newEmail, setNewEmail] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    
    // Filter states
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const handleUpdateQuota = (record: any) => {
        setSelectedUser(record);
        setNewQuotaLimit(record.quotaLimit);
        setNewQuotaUsed(record.quotaUsed);
        setQuotaModalVisible(true);
    };

    const handleUpdateRole = (record: any) => {
        setSelectedUser(record);
        setNewRole(record.role);
        setRoleModalVisible(true);
    };

    const handleUpdateEmail = (record: any) => {
        setSelectedUser(record);
        setNewEmail(record.email);
        setEmailModalVisible(true);
    };

    const handleResetPassword = (record: any) => {
        setSelectedUser(record);
        setNewPassword('');
        setPasswordModalVisible(true);
    };

    const confirmUpdateQuota = async () => {
        if (!selectedUser) return;
        
        try {
            const response = await updateUserQuota(selectedUser.username, newQuotaLimit, newQuotaUsed);
            const result = await response.json();
            
            if (result.success) {
                message.success('配額已更新');
                setQuotaModalVisible(false);
                fetchData();
            } else {
                message.error(result.message || '更新配額失敗');
            }
        } catch (err) {
            message.error('更新配額失敗');
        }
    };

    const confirmUpdateRole = async () => {
        if (!selectedUser) return;
        
        try {
            const response = await updateUserRole(selectedUser.username, newRole);
            const result = await response.json();
            
            if (result.success) {
                message.success('角色已更新');
                setRoleModalVisible(false);
                fetchData();
            } else {
                message.error(result.message || '更新角色失敗');
            }
        } catch (err) {
            message.error('更新角色失敗');
        }
    };

    const confirmUpdateEmail = async () => {
        if (!selectedUser) return;
        
        if (!newEmail || !newEmail.includes('@')) {
            message.error('請輸入有效的電郵地址');
            return;
        }
        
        try {
            const response = await updateUserEmail(selectedUser.username, newEmail);
            const result = await response.json();
            
            if (result.success) {
                message.success('電郵已更新');
                setEmailModalVisible(false);
                fetchData();
            } else {
                message.error(result.message || '更新電郵失敗');
            }
        } catch (err) {
            message.error('更新電郵失敗');
        }
    };

    const confirmResetPassword = async () => {
        if (!selectedUser) return;
        
        if (!newPassword || newPassword.length < 4) {
            message.error('密碼至少需要4個字符');
            return;
        }
        
        try {
            const response = await resetUserPassword(selectedUser.username, newPassword);
            const result = await response.json();
            
            if (result.success) {
                message.success('密碼已重置');
                setPasswordModalVisible(false);
                setNewPassword('');
            } else {
                message.error(result.message || '重置密碼失敗');
            }
        } catch (err) {
            message.error('重置密碼失敗');
        }
    };

    const handleClearSubmissions = async (username: string) => {
        try {
            const response = await clearUserSubmissions(username);
            const result = await response.json();
            
            if (result.success) {
                message.success('提交紀錄已清除');
                fetchData();
            } else {
                message.error('清除提交紀錄失敗');
            }
        } catch (err) {
            message.error('清除提交紀錄失敗');
        }
    };

    const handleDelete = async (username: string) => {
        try {
            const response = await deleteUserAccount(username);
            const result = await response.json();
            
            if (result.success) {
                message.success('用戶已刪除');
                fetchData();
            } else {
                message.error(result.message || '刪除用戶失敗');
            }
        } catch (err) {
            message.error('刪除用戶失敗');
        }
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnTitle = (dataIndex: DataIndex): string => {
        const titles: Record<DataIndex, string> = {
            username: '用戶名稱',
            email: '電郵地址',
            agencyName: '旅行社名稱',
            role: '角色',
            quotaLimit: '配額上限',
            quotaUsed: '已使用配額',
        };
        return titles[dataIndex] || dataIndex;
    };

    const getColumnSearchProps = (dataIndex: DataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`搜尋 ${getColumnTitle(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        搜尋
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        重置
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        關閉
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value: any, record: any) => {
            const recordValue = record[dataIndex];
            return recordValue
                ? recordValue.toString().toLowerCase().includes(value.toLowerCase())
                : false;
        },
        onFilterDropdownOpenChange: (visible: boolean) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text: string) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const getRoleFilterProps = () => ({
        filters: [
            { text: '用戶', value: 1 },
            { text: '管理員', value: 2 },
            { text: '超級管理員', value: 3 },
        ],
        onFilter: (value: any, record: any) => record.role === value,
    });

    const columns: ColumnsType<DataType> = [
        {
            title: '用戶名稱',
            dataIndex: 'username',
            key: 'username',
            sorter: (a: any, b: any) => a.username.localeCompare(b.username),
            showSorterTooltip: false,
            width: 150,
            ...getColumnSearchProps('username'),
            render: (text: string) => {
                const displayText = searchedColumn === 'username' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : text;
                return (
                    <span style={{ fontWeight: '500', color: '#2c3e50' }}>
                        <UserOutlined style={{ marginRight: '8px', color: '#667eea' }} />
                        {displayText}
                    </span>
                );
            },
        },
        {
            title: '電郵地址',
            dataIndex: 'email',
            key: 'email',
            sorter: (a: any, b: any) => a.email.localeCompare(b.email),
            showSorterTooltip: false,
            width: 200,
            ...getColumnSearchProps('email'),
        },
        {
            title: '旅行社名稱',
            dataIndex: 'agencyName',
            key: 'agencyName',
            sorter: (a: any, b: any) => (a.agencyName || '').localeCompare(b.agencyName || ''),
            showSorterTooltip: false,
            width: 180,
            ...getColumnSearchProps('agencyName'),
            render: (text: string) => {
                const displayText = searchedColumn === 'agencyName' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (text || '-');
                return (
                    <span style={{ color: '#5a6c7d' }}>{displayText}</span>
                );
            },
        },
        {
            title: '角色',
            dataIndex: 'role',
            key: 'role',
            sorter: (a: any, b: any) => a.role - b.role,
            showSorterTooltip: false,
            width: 120,
            ...getRoleFilterProps(),
            render: (role: number) => (
                <Tag color={getRoleColor(role)} style={{ fontWeight: '500' }}>
                    {getRoleName(role)}
                </Tag>
            ),
        },
        {
            title: '配額使用',
            key: 'quotaUsage',
            width: 220,
            sorter: (a: any, b: any) => {
                const percentA = a.quotaLimit > 0 ? (a.quotaUsed / a.quotaLimit) * 100 : 0;
                const percentB = b.quotaLimit > 0 ? (b.quotaUsed / b.quotaLimit) * 100 : 0;
                return percentA - percentB;
            },
            showSorterTooltip: false,
            render: (text: string, record: any) => {
                const percent = record.quotaLimit > 0 
                    ? Math.round((record.quotaUsed / record.quotaLimit) * 100) 
                    : 0;
                const strokeColor = percent >= 90 ? '#ff4d4f' : percent >= 70 ? '#faad14' : '#52c41a';
                
                return (
                    <div style={{ minWidth: '200px' }}>
                        <div style={{ marginBottom: '4px', fontSize: '12px', color: '#5a6c7d' }}>
                            {record.quotaUsed} / {record.quotaLimit}
                        </div>
                        <Progress 
                            percent={percent} 
                            strokeColor={strokeColor}
                            size="small"
                            status={percent >= 100 ? 'exception' : 'normal'}
                        />
                    </div>
                );
            },
        },
        {
            title: '動作',
            key: 'action',
            fixed: 'right',
            width: 350,
            render: (text: string, record: any) => (
                <Space size="small" wrap>
                    <Tooltip title="修改配額">
                        <Button
                            icon={<DashboardOutlined />}
                            size="small"
                            onClick={() => handleUpdateQuota(record)}
                        >
                            配額
                        </Button>
                    </Tooltip>
                    <Tooltip title="修改角色">
                        <Button
                            icon={<SafetyOutlined />}
                            size="small"
                            onClick={() => handleUpdateRole(record)}
                        >
                            角色
                        </Button>
                    </Tooltip>
                    <Tooltip title="修改電郵">
                        <Button
                            icon={<MailOutlined />}
                            size="small"
                            onClick={() => handleUpdateEmail(record)}
                        >
                            電郵
                        </Button>
                    </Tooltip>
                    <Tooltip title="重置密碼">
                        <Button
                            icon={<LockOutlined />}
                            size="small"
                            onClick={() => handleResetPassword(record)}
                        >
                            密碼
                        </Button>
                    </Tooltip>
                    <Popconfirm
                        title="確定要清除此用戶的所有提交紀錄嗎？"
                        onConfirm={() => handleClearSubmissions(record.username)}
                        okText="確定"
                        cancelText="取消"
                    >
                        <Tooltip title="清除提交紀錄">
                            <Button
                                icon={<ClearOutlined />}
                                size="small"
                                type="default"
                            >
                                清除
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                    <Popconfirm
                        title="確定要刪除此用戶嗎？此操作無法撤銷！"
                        onConfirm={() => handleDelete(record.username)}
                        okText="確定"
                        cancelText="取消"
                    >
                        <Tooltip title="刪除用戶">
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            >
                                刪除
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    async function fetchData() {
        setLoading(true);
        try {
            const response = await fetchAllAcounts();
            if (response.status === 401) {
                if (setLoggedIn) {
                    setLoggedIn(false);
                }
            }
            else if (response.status === 403) {
                message.error('您沒有權限訪問此頁面');
                return;
            }
            else if (response.status !== 200) {
                console.log('Failed to fetch accounts');
                return;
            }
            const result = await response.json();
            if (!result.success) {
                console.log('Failed to fetch accounts');
                return;
            }
            setAccounts(result.data);
        } catch (err) {
            console.log('Failed to fetch accounts');
            message.error('無法取得用戶列表');
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <div style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
            <div style={{
                marginBottom: '24px',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
                <h2 style={{
                    margin: 0,
                    fontSize: '26px',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}>用戶管理</h2>
            </div>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
                <Table
                    rowKey="username"
                    columns={columns}
                    dataSource={accounts}
                    size="small"
                    loading={loading}
                    scroll={{ x: 1400 }}
                    pagination={{
                        pageSizeOptions: ['10', '20', '30', '50'],
                        showSizeChanger: true,
                        showTotal: (total) => `總共 ${total} 位用戶`,
                    }}
                />
            </div>

            <Modal
                title={`修改配額 - ${selectedUser?.username}`}
                open={quotaModalVisible}
                onOk={confirmUpdateQuota}
                onCancel={() => setQuotaModalVisible(false)}
                okText="確定"
                cancelText="取消"
            >
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        配額上限:
                    </label>
                    <InputNumber
                        min={0}
                        value={newQuotaLimit}
                        onChange={(value) => setNewQuotaLimit(value || 0)}
                        style={{ width: '100%' }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        已使用配額:
                    </label>
                    <InputNumber
                        min={0}
                        value={newQuotaUsed}
                        onChange={(value) => setNewQuotaUsed(value || 0)}
                        style={{ width: '100%' }}
                    />
                </div>
            </Modal>

            <Modal
                title={`修改角色 - ${selectedUser?.username}`}
                open={roleModalVisible}
                onOk={confirmUpdateRole}
                onCancel={() => setRoleModalVisible(false)}
                okText="確定"
                cancelText="取消"
            >
                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        選擇角色:
                    </label>
                    <Select
                        value={newRole}
                        onChange={setNewRole}
                        style={{ width: '100%' }}
                    >
                        <Select.Option value={1}>用戶</Select.Option>
                        <Select.Option value={2}>管理員</Select.Option>
                        <Select.Option value={3}>超級管理員</Select.Option>
                    </Select>
                </div>
            </Modal>

            <Modal
                title={`修改電郵 - ${selectedUser?.username}`}
                open={emailModalVisible}
                onOk={confirmUpdateEmail}
                onCancel={() => setEmailModalVisible(false)}
                okText="確定"
                cancelText="取消"
            >
                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        新電郵地址:
                    </label>
                    <Input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="請輸入電郵地址"
                    />
                </div>
            </Modal>

            <Modal
                title={`重置密碼 - ${selectedUser?.username}`}
                open={passwordModalVisible}
                onOk={confirmResetPassword}
                onCancel={() => setPasswordModalVisible(false)}
                okText="確定"
                cancelText="取消"
            >
                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        新密碼 (至少4個字符):
                    </label>
                    <Input.Password
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="請輸入新密碼"
                    />
                </div>
            </Modal>
        </div>
    );
};

export default AccountsPage;
